import { useState, useEffect, useCallback } from "react";

// Coach-mark tour state + persistence. Separate from useFtue so the
// modal product tour and the coach-mark tour can run independently
// (and complete independently) without one's completion flag
// silencing the other.
//
// Trigger logic:
//   - Auto-opens 1.2s after mount IF the modal FTUE for this user
//     has already been marked complete (so we don't pile the coach
//     marks on top of the welcome modal).
//   - Stays closed if its own completion flag is set.
//
// Storage keys:
//   ftue_<role>_<userId>       — modal welcome tour completion
//   coach_<role>_<userId>      — coach-mark tour completion
export default function useCoachMarkTour(role, userId) {
  const ftueKey = role && userId ? `ftue_${role}_${userId}` : null;
  const coachKey = role && userId ? `coach_${role}_${userId}` : null;
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!coachKey) { setIsOpen(false); return; }
    let coachDone = "1";
    let ftueDone = null;
    try {
      coachDone = window.localStorage.getItem(coachKey);
      ftueDone = window.localStorage.getItem(ftueKey);
    } catch (_) { coachDone = "1"; }
    // Don't auto-open if:
    //   - already completed coach marks
    //   - modal tour hasn't been completed yet (let it finish first)
    if (coachDone) { setIsOpen(false); return; }
    if (!ftueDone) { setIsOpen(false); return; }
    // Delay a bit so the screen has settled after the modal tour
    // closes (and so bottom-nav refs are mounted).
    const id = setTimeout(() => setIsOpen(true), 1200);
    return () => clearTimeout(id);
  }, [coachKey, ftueKey]);

  const mark = useCallback(() => {
    if (!coachKey) return;
    try { window.localStorage.setItem(coachKey, "1"); } catch (_) {}
    setIsOpen(false);
  }, [coachKey]);

  const complete = useCallback(() => mark(), [mark]);
  const skip = useCallback(() => mark(), [mark]);

  // Manual re-trigger from a "Show me how" menu later.
  const replay = useCallback(() => {
    if (!coachKey) return;
    try { window.localStorage.removeItem(coachKey); } catch (_) {}
    setIsOpen(true);
  }, [coachKey]);

  return { isOpen, complete, skip, replay };
}
